import { Injectable, signal } from '@angular/core';

import {
  ADMIN_PERMISSIONS,
  Permission,
  PermissionCheckResult,
  SUPERADMIN_PERMISSIONS,
} from '../models/permission.model';

/**
 * User role type for permission derivation
 */
type UserRole = 'SuperAdmin' | 'Admin' | null;

/**
 * Reactive store for user permissions.
 * Provides permission checking methods and integrates with role-based access control.
 *
 * @example
 * ```typescript
 * const permissionStore = inject(PermissionStore);
 *
 * // Set permissions after login (from role)
 * permissionStore.setPermissionsFromRole('Admin');
 *
 * // Check permission
 * if (permissionStore.hasPermission('transaction:cancel')) {
 *   // User can cancel transactions
 * }
 *
 * // Check with reason for denial
 * const result = permissionStore.checkPermission('role:create');
 * if (!result.allowed) {
 *   console.log(result.reason); // "Requires SuperAdmin role"
 * }
 * ```
 */
@Injectable({ providedIn: 'root' })
export class PermissionStore {
  private readonly _permissions = signal<Permission[]>([]);
  private readonly _loading = signal(false);
  private readonly _currentRole = signal<UserRole>(null);

  /**
   * Current user permissions (readonly signal)
   */
  readonly permissions = this._permissions.asReadonly();

  /**
   * Loading state for async permission operations
   */
  readonly loading = this._loading.asReadonly();

  /**
   * Current user role (readonly signal)
   */
  readonly currentRole = this._currentRole.asReadonly();

  /**
   * Check if user has a specific permission
   * @param permission - Permission string (e.g., "transaction:cancel")
   * @returns true if user has the permission
   */
  hasPermission(permission: Permission): boolean {
    return this._permissions().includes(permission);
  }

  /**
   * Check permission with detailed result including denial reason
   * @param permission - Permission string to check
   * @returns PermissionCheckResult with allowed flag and optional reason
   */
  checkPermission(permission: Permission): PermissionCheckResult {
    const allowed = this.hasPermission(permission);
    if (allowed) {
      return { allowed: true };
    }

    // Parse permission to provide helpful denial reason
    const [resource, action] = permission.split(':') as [string, string];
    const role = this._currentRole();

    if (!role) {
      return {
        allowed: false,
        reason: 'Authentication required',
      };
    }

    // Check if this is a role-restricted permission
    if (resource === 'role' && action !== 'view') {
      return {
        allowed: false,
        reason: 'Requires SuperAdmin role',
      };
    }

    return {
      allowed: false,
      reason: `Requires ${action} permission for ${resource}`,
    };
  }

  /**
   * Check if user has any of the given permissions
   * @param permissions - Array of permissions to check
   * @returns true if user has at least one of the permissions
   */
  hasAnyPermission(permissions: Permission[]): boolean {
    return permissions.some((p) => this.hasPermission(p));
  }

  /**
   * Check if user has all of the given permissions
   * @param permissions - Array of permissions to check
   * @returns true only if user has all permissions
   */
  hasAllPermissions(permissions: Permission[]): boolean {
    return permissions.every((p) => this.hasPermission(p));
  }

  /**
   * Set permissions directly (useful for custom permission sets)
   * @param permissions - Array of permissions to set
   */
  setPermissions(permissions: Permission[]): void {
    this._permissions.set(permissions);
  }

  /**
   * Set permissions based on user role (MVP implementation)
   * SuperAdmin gets all permissions, Admin gets all except role management
   * @param role - User role from AuthStore
   */
  setPermissionsFromRole(role: UserRole): void {
    this._currentRole.set(role);

    switch (role) {
      case 'SuperAdmin':
        this._permissions.set([...SUPERADMIN_PERMISSIONS]);
        break;
      case 'Admin':
        this._permissions.set([...ADMIN_PERMISSIONS]);
        break;
      default:
        this._permissions.set([]);
    }
  }

  /**
   * Clear all permissions (called on logout)
   */
  clearPermissions(): void {
    this._permissions.set([]);
    this._currentRole.set(null);
  }

  /**
   * Set loading state for async operations
   * @param loading - Loading state
   */
  setLoading(loading: boolean): void {
    this._loading.set(loading);
  }
}
